import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { listOrgs } from '@/api/orgs';
import { ORG_STATUSES, type OrgStatus } from '@/api/orgTypes';
import { partnerOptions } from '@/api/partners';
import { listPlans } from '@/api/plans';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';
import { OrgTable } from './OrgTable';

const ALL = 'ALL';

export function OrgsListPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>(ALL);
  const [partnerId, setPartnerId] = useState<string>(ALL);
  const [planId, setPlanId] = useState<string>(ALL);
  const [page, setPage] = useState(0);

  const partners = useQuery({ queryKey: ['partners', 'options'], queryFn: partnerOptions });
  const plans = useQuery({ queryKey: ['plans'], queryFn: listPlans });

  const query = useQuery({
    queryKey: ['orgs', { q, status, partnerId, planId, page }],
    queryFn: () =>
      listOrgs({
        ...(q ? { q } : {}),
        ...(status !== ALL ? { status: status as OrgStatus } : {}),
        ...(partnerId !== ALL ? { partner_id: partnerId } : {}),
        ...(planId !== ALL ? { plan_id: planId } : {}),
        page,
      }),
  });

  const filter = (setter: (value: string) => void) => (value: string) => {
    setPage(0);
    setter(value);
  };
  const allPlans = [...(plans.data?.platform_plans ?? []), ...(plans.data?.partner_plans ?? [])];

  return (
    <>
      <PageHeader title="Organizations" description="Every organization, across all partners." />

      <div className="flex flex-wrap gap-2">
        <Input
          aria-label="Search organizations"
          placeholder="Search by name, reference or id"
          className="max-w-xs"
          value={q}
          onChange={(event) => filter(setQ)(event.target.value)}
        />
        <Select value={status} onValueChange={filter(setStatus)}>
          <SelectTrigger aria-label="Status" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {ORG_STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={partnerId} onValueChange={filter(setPartnerId)}>
          <SelectTrigger aria-label="Partner" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All partners</SelectItem>
            {partners.data?.map((partner) => (
              <SelectItem key={partner.id} value={partner.id}>
                {partner.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={planId} onValueChange={filter(setPlanId)}>
          <SelectTrigger aria-label="Plan" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All plans</SelectItem>
            {allPlans.map((plan) => (
              <SelectItem key={plan.id} value={plan.id}>
                {plan.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <OrgTable
          page={query.data}
          isLoading={query.isPending}
          onPage={setPage}
          empty="No organizations match those filters."
        />
      )}
    </>
  );
}
